import { FormEvent, useEffect, useState } from 'react'
import AudioArchiveApp from './new/AudioArchiveApp'
import { apiBaseUrl, getToken, setToken } from './new/api'
import { consumeSsoToken } from './new/deep-link'

export default function App() {
  // 从华仁谋士跳过来时带着登录 token（见 new/deep-link.ts）：先存下、从地址栏抹掉，再走下面的 /api/verify 校验
  const [token, setSessionToken] = useState(() => {
    const sso = consumeSsoToken()
    if (sso) setToken(sso)
    return getToken()
  })
  const [checking, setChecking] = useState(Boolean(token))
  const [error, setError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (!token) return
    fetch(`${apiBaseUrl}/api/verify`, { headers: { Authorization: `Bearer ${token}` } })
      .then(response => response.json())
      .then(data => {
        if (!data.authenticated) {
          // 从华仁谋士带过来的登录态（或本地存的）已失效：回到登录页并说明，深链参数还在地址栏里，登录后接着打开
          setToken(null)
          setSessionToken(null)
          setError('登录已失效，请重新登录。')
        }
      })
      .catch(() => setError('暂时无法连接音频库，请稍后重试。'))
      .finally(() => setChecking(false))
  }, [token])

  if (checking) return <div className="archive-loading" role="status" aria-live="polite">正在连接华仁音频库…</div>

  if (token) {
    return <AudioArchiveApp token={token} onLogout={() => { setToken(null); setSessionToken(null) }} />
  }

  const login = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (loginLoading) return
    const form = new FormData(event.currentTarget)
    setError('')
    setLoginLoading(true)
    try {
      const response = await fetch(`${apiBaseUrl}/api/login-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: form.get('username'), password: form.get('password') })
      })
      const data = await response.json()
      if (!response.ok || !data.token) {
        setError(data.error || data.message || '登录失败，请检查账号和密码。')
        return
      }
      setChecking(true)
      setToken(data.token)
      setSessionToken(data.token)
    } catch {
      setError('暂时无法连接音频库，请稍后重试。')
    } finally {
      setLoginLoading(false)
    }
  }

  return (
    <main className="login-shell">
      <div className="login-atmosphere" aria-hidden="true"><i /><i /><span /></div>
      <section className="desktop-login-story" aria-label="华仁音频库介绍"><p>华仁 · 声音档案</p><h2>让值得留下的声音，<br />陪你走得更远。</h2><span>收听、回顾、发现。<br />在熟悉的声音里，遇见新的思考。</span><div className="login-story-art" aria-hidden="true" /><small>留一段时间，听见更多。</small></section>
      <form className="login-card login-redesign" onSubmit={login} aria-busy={loginLoading}>
        <header className="login-heading"><p>华仁音频库</p><h1>欢迎回来</h1></header>
        <div className="login-fields">
          <label className="login-field"><span className="login-field-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="3" /><path d="M5 21c.6-4 3-6 7-6s6.4 2 7 6" /></svg></span><input name="username" required autoComplete="username" disabled={loginLoading} placeholder="用户名" aria-label="用户名" /></label>
          <label className="login-field"><span className="login-field-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v2" /></svg></span><input name="password" required type={showPassword ? 'text' : 'password'} autoComplete="current-password" disabled={loginLoading} placeholder="密码" aria-label="密码" /><button type="button" className="password-visibility" aria-label={showPassword ? '隐藏密码' : '显示密码'} onClick={() => setShowPassword(visible => !visible)} disabled={loginLoading}><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M2.5 12s3.3-5 9.5-5 9.5 5 9.5 5-3.3 5-9.5 5-9.5-5-9.5-5Z" /><circle cx="12" cy="12" r="2.5" />{!showPassword && <path d="m4 4 16 16" />}</svg></button></label>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="solid-button login-submit" type="submit" disabled={loginLoading}>{loginLoading && <span className="button-spinner" aria-hidden="true" />}{loginLoading ? '正在登录…' : '登录'}</button>
        <p className="login-assistance">使用你的账号继续回顾</p>
      </form>
    </main>
  )
}
